/* @jsx h */
import { api } from "@app/data/api";
import { Loading } from "@app/vue/components/UIComponents";
import { ResponsiveTransactionList } from "@app/vue/pages/TransactionHistory/components/ResponsiveTransactionList";
import { PrintReceiptModal } from "@app/vue/pages/TransactionHistory/PrintReceiptModal";
import { CreditNoteModal } from "@app/vue/pages/TransactionHistory/components/credit-note/CreditNoteModal";
import { useCreditNoteSubmit } from "@app/vue/pages/TransactionHistory/components/credit-note/useCreditNoteSubmit";
import type { CreditNoteSubmit } from "@app/vue/pages/TransactionHistory/components/credit-note/types";
import { EditPaymentModal } from "@app/vue/pages/TransactionHistory/components/EditPaymentModal";
import { DigitalReceiptModal } from "@app/vue/components/CheckoutComponent/DigitalReceiptModal";
import { useToast } from "../../../../../providers/index";
import { useLocalization } from "@lemmonhq/uikit";
import { Payment, TableStatus, RawPayment } from "@lemmonhq/types";
import { defineComponent, h, onMounted, ref } from "vue";

export type TablePaymentsListProps = {
  tableStatus: TableStatus;
};
export const TablePaymentsList = defineComponent<TablePaymentsListProps>({
  name: "TablePaymentsList",
  props: ["tableStatus"],
  setup(props) {
    const paymentsList = ref<Payment[]>([]);
    const selectedPaymentId = ref<number | null>(null);
    const showCreditNoteModal = ref<RawPayment>();
    const submitCreditNote = useCreditNoteSubmit();
    const showEditPaymentModal = ref<RawPayment>();
    const showDigitalReceipt = ref<RawPayment>();
    const { showToast } = useToast();
    const { t } = useLocalization();
    const loading = ref<boolean | Error>(false);

    const fetch = async () => {
      loading.value = true;
      try {
        const orderIds = props.tableStatus.orders.map((el) => el.id);
        if (orderIds.length == 0) {
          loading.value = false;
          return;
        }
        const rawPayments = await api.getTablePayments(orderIds);
        const payments = await Promise.all(
          rawPayments.payments.map(async (p) => {
            return await api.getPayment(p.id);
          }),
        );
        paymentsList.value = payments;
        loading.value = false;
      } catch (e: any) {
        loading.value = new Error(e.message || "Failed loading payments");
      } finally {
      }
    };

    const handleCreditNote = async (input: CreditNoteSubmit) => {
      const payment = showCreditNoteModal.value;
      if (!payment) return;
      const result = await submitCreditNote(payment, input);
      if (!result) return;
      showCreditNoteModal.value = undefined;
      fetch(); // Refresh the list
    };

    const handleEditPayment = async (
      id: string,
      data: { payment_method: string },
    ) => {
      try {
        await api.updatePayment(id, data);
        showToast(t("general.success_notification.payment_edited"), {
          type: "success",
        });
        showEditPaymentModal.value = undefined;
        fetch(); // Refresh the list
      } catch (error) {
        console.error("Failed to edit payment", error);
        showToast("Failed to edit payment", { type: "error" });
      }
    };

    onMounted(() => {
      fetch();
    });

    return () => {
      if (loading.value == true) {
        return (
          <div class="flex flex-1 justify-center items-center">
            <Loading />
          </div>
        );
      } else if (loading.value instanceof Error) {
        return (
          <div class="flex flex-1 justify-center items-center">
            {loading.value.message}
          </div>
        );
      } else {
        if (paymentsList.value.length === 0) {
          return (
            <div class="flex flex-col text-center justify-center items-center flex-1 gap-2 max-w-72 mx-auto h-full">
              <h2 class="text-2xl font-bold text-accent">
                No payments for this table.
              </h2>
              <h3 class="text-xl">
                Click on pay button to add payments for these orders.
              </h3>
            </div>
          );
        }
        return (
          <>
            <ResponsiveTransactionList
              showDetailsInModal={true}
              onPrintReceipt={(id: number) => (selectedPaymentId.value = id)}
              onShowEditPayment={(payment: RawPayment) => {
                showEditPaymentModal.value = payment;
              }}
              onShowCreateCreditNode={(paymentId: number) => {
                showCreditNoteModal.value = paymentsList.value.find(
                  (p) => p.id === paymentId,
                );
              }}
              onShowDigitalReceipt={(payment: RawPayment) => {
                showDigitalReceipt.value = payment;
              }}
              transactions={paymentsList.value}
              onPaymentShow={() => false}
            />

            {selectedPaymentId.value && (
              <PrintReceiptModal
                paymentId={selectedPaymentId.value}
                onClose={() => (selectedPaymentId.value = null)}
              />
            )}

            {showCreditNoteModal.value && (
              <CreditNoteModal
                key={showCreditNoteModal.value.id}
                payment={showCreditNoteModal.value}
                initialReopen={false}
                onClose={() => (showCreditNoteModal.value = undefined)}
                onSubmit={handleCreditNote}
              />
            )}

            {showEditPaymentModal.value && (
              <EditPaymentModal
                onClose={() => (showEditPaymentModal.value = undefined)}
                onSubmit={handleEditPayment}
                payment={showEditPaymentModal.value}
              />
            )}

            {showDigitalReceipt.value ? (
              <DigitalReceiptModal
                paymentId={showDigitalReceipt.value.id}
                paymentGuid={showDigitalReceipt.value.guid}
                total={
                  showDigitalReceipt.value.amount +
                  (showDigitalReceipt.value.tips || 0)
                }
                onClose={() => (showDigitalReceipt.value = undefined)}
              />
            ) : null}
          </>
        );
      }
    };
  },
});
